// Phase 259 — Super Admin Adoption & Presence Cockpit.
// Tracks who is online right now, who is stuck (never logged in), and lets
// the platform owner push setup-help via Email + WhatsApp with one click.
import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Activity, Radio, AlertCircle, Send, Loader2, Users, CheckCircle, Mail, MessageCircle, AlertTriangle, Zap } from 'lucide-react';
import { toast } from 'sonner';
import PwaFunnelTile from '@/components/admin/PwaFunnelTile';
import PushEngagementTile from '@/components/admin/PushEngagementTile';
// Phase 282d — Live + locked quarterly star academy leaderboard
import QuarterlyStarLeaderboardTile from '@/components/admin/QuarterlyStarLeaderboardTile';

const ROLE_BADGE = {
  student: 'bg-blue-100 text-blue-700 border-blue-200',
  parent: 'bg-pink-100 text-pink-700 border-pink-200',
  coach: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  employee: 'bg-amber-100 text-amber-700 border-amber-200',
  manager: 'bg-violet-100 text-violet-700 border-violet-200',
  academy_owner: 'bg-slate-900 text-white border-slate-900',
};

export default function AdminAdoption() {
  const [metrics, setMetrics] = useState(null);
  const [stuck, setStuck] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(true);
  const [loadingStuck, setLoadingStuck] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [processingId, setProcessingId] = useState(null);
  // Phase 259b — Bulk Push Setup
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkChannels, setBulkChannels] = useState({ email: true, whatsapp: true });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const loadMetrics = () => {
    setLoadingMetrics(true);
    api.get('/super-admin/adoption/metrics')
      .then(r => setMetrics(r.data))
      .catch(() => toast.error('Failed to load adoption metrics'))
      .finally(() => setLoadingMetrics(false));
  };

  const loadStuck = (role) => {
    setLoadingStuck(true);
    const url = role && role !== 'all'
      ? `/super-admin/adoption/stuck-users?limit=50&role=${role}`
      : '/super-admin/adoption/stuck-users?limit=50';
    api.get(url)
      .then(r => setStuck(r.data))
      .catch(() => setStuck({ count: 0, users: [] }))
      .finally(() => setLoadingStuck(false));
  };

  useEffect(() => { loadMetrics(); }, []);
  useEffect(() => { loadStuck(roleFilter); }, [roleFilter]);

  const handlePushOutreach = async (user, channel = 'both') => {
    setProcessingId(user.id);
    try {
      const res = await api.post('/super-admin/adoption/outreach', {
        user_id: user.id,
        channel,
      });
      const succ = res.data?.channels_succeeded || [];
      const errs = res.data?.errors || {};
      if (succ.length > 0) {
        const labels = succ.map(c => c === 'email' ? 'Email' : 'WhatsApp').join(' + ');
        toast.success(`Setup help pushed to ${user.name} via ${labels}`);
      }
      Object.entries(errs).forEach(([ch, reason]) => {
        const chLabel = ch === 'email' ? 'Email' : 'WhatsApp';
        const friendly = reason === 'no_email_on_file' ? 'no email on file'
                       : reason === 'no_phone_on_file' ? 'no phone on file'
                       : `failed (${String(reason).slice(0, 60)})`;
        toast.error(`${chLabel}: ${friendly}`);
      });
      if (succ.length === 0 && Object.keys(errs).length === 0) {
        toast.error('No channels dispatched');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Outreach failed');
    } finally {
      setProcessingId(null);
    }
  };

  if (loadingMetrics) {
    return (
      <div className="space-y-4 p-6">
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="admin-adoption-page">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-4">
        <h1 className="text-xl font-display font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
          <Activity className="w-5 h-5 text-emerald-600" />
          Adoption & Presence Cockpit
        </h1>
        <p className="text-xs text-muted-foreground mt-1">
          Real-time platform usage across every academy. Push setup help to users who have never opened the portal.
        </p>
      </div>

      {/* Top metrics grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="adoption-metrics-grid">
        <Card className="border-emerald-200 dark:border-emerald-800 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
          <CardContent className="p-4 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-emerald-700 dark:text-emerald-300">
              <Radio className="w-3 h-3 animate-pulse" /> Online Now
            </div>
            <div className="text-3xl font-display font-bold tabular-nums text-emerald-700 dark:text-emerald-200" data-testid="metric-online-now">{metrics.online_now}</div>
            <p className="text-[10px] text-muted-foreground">Active in last 10 minutes</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-1">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Adoption Rate</div>
            <div className="text-3xl font-display font-bold tabular-nums text-slate-900 dark:text-slate-100" data-testid="metric-adoption-rate">{metrics.adoption_rate_pct}%</div>
            <p className="text-[10px] text-muted-foreground">{metrics.total_users - metrics.stuck_never_active} of {metrics.total_users} ever logged in</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200 dark:border-slate-700">
          <CardContent className="p-4 space-y-1">
            <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500">Active (30 days)</div>
            <div className="text-3xl font-display font-bold tabular-nums text-slate-900 dark:text-slate-100" data-testid="metric-active-30d">{metrics.active_30d}</div>
            <p className="text-[10px] text-muted-foreground">{metrics.total_academies} academies live</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 dark:border-amber-800 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30">
          <CardContent className="p-4 space-y-1">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-wider text-amber-700 dark:text-amber-300">
              <AlertCircle className="w-3 h-3" /> Stuck — Never Active
            </div>
            <div className="text-3xl font-display font-bold tabular-nums text-amber-700 dark:text-amber-200" data-testid="metric-stuck">{metrics.stuck_never_active}</div>
            <p className="text-[10px] text-muted-foreground">No last_active stamp</p>
          </CardContent>
        </Card>
      </div>

      {/* Segment breakdown */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-slate-500 flex items-center gap-1.5">
            <Users className="w-3 h-3" /> Segment Breakdown
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 text-xs">
            {Object.entries(metrics.by_role).map(([role, count]) => (
              <div key={role} className="rounded-lg border border-slate-200 dark:border-slate-700 p-2.5 text-center" data-testid={`segment-${role}`}>
                <p className="text-[10px] text-muted-foreground uppercase font-semibold tracking-wider">{role.replace('_', ' ')}</p>
                <p className="text-lg font-bold tabular-nums text-slate-900 dark:text-slate-100">{count}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Phase 261d — PWA install funnel tile */}
      <PwaFunnelTile />

      {/* Phase 261l — Push notification engagement tile */}
      <PushEngagementTile />

      {/* Phase 282d — Quarterly Star Academy leaderboard (live + locked) */}
      <QuarterlyStarLeaderboardTile />

      {/* Stuck users roster */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-bold flex items-center gap-1.5"><AlertCircle className="w-4 h-4 text-amber-600" /> Profiles Requiring Setup Assistance</h3>
              <p className="text-[11px] text-muted-foreground">Users with no last_active stamp — never opened the portal.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Filter Role</span>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-36 h-8 text-xs" data-testid="adoption-role-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All segments</SelectItem>
                  <SelectItem value="academy_owner">Academy Owner</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="h-8 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                onClick={() => { setBulkOpen(true); setBulkPreview(null); setBulkResult(null); setBulkChannels({ email: true, whatsapp: true }); }}
                data-testid="bulk-push-setup-open"
              >
                <Zap className="w-3.5 h-3.5 mr-1" /> Bulk Push Setup
              </Button>
            </div>
          </div>

          {loadingStuck ? (
            <Skeleton className="h-48 rounded-lg" />
          ) : (!stuck || stuck.users.length === 0) ? (
            <div className="p-8 text-center text-muted-foreground text-sm border border-slate-200 dark:border-slate-700 rounded-lg">
              <CheckCircle className="w-8 h-8 mx-auto text-emerald-500 mb-2" />
              All user nodes have processed initial logins for this filter.
            </div>
          ) : (
            <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-[10px] uppercase tracking-wider">User</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Role</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Academy</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider">Contact</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-wider text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stuck.users.map(u => (
                    <TableRow key={u.id} data-testid={`stuck-row-${u.id}`}>
                      <TableCell>
                        <div className="font-bold text-sm">{u.name}</div>
                        <div className="text-[10px] text-muted-foreground font-mono truncate max-w-[200px]">{u.email}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[10px] uppercase font-bold ${ROLE_BADGE[u.role] || 'bg-slate-100 text-slate-700'}`}>
                          {u.role.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">{u.academy_name || <span className="text-muted-foreground italic">none</span>}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-[11px]">
                          {u.email && <Mail className="w-3 h-3 text-emerald-600" title={u.email} />}
                          {u.phone && <MessageCircle className="w-3 h-3 text-emerald-600" title={u.phone} />}
                          {!u.email && !u.phone && <span className="text-amber-600 italic">no contact</span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          disabled={processingId === u.id || (!u.email && !u.phone)}
                          onClick={() => handlePushOutreach(u, 'both')}
                          className="h-7 text-[11px] bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900"
                          data-testid={`stuck-outreach-${u.id}`}
                        >
                          {processingId === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Push Setup</>}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {stuck && stuck.count >= 50 && (
            <p className="text-[11px] text-muted-foreground italic">
              Showing first 50 stuck users. Apply a role filter to narrow the list.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Phase 259b — Bulk Push Setup dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o) { setBulkOpen(false); setBulkPreview(null); setBulkResult(null); setBulkLoading(false); } }}>
        <DialogContent className="max-w-lg" data-testid="bulk-push-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-amber-500" /> Bulk Push Setup</DialogTitle>
            <DialogDescription className="text-xs">
              Dispatch setup-help (Email + WhatsApp) to every stuck user matching the role filter currently applied.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              {/* Scope summary */}
              <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 text-xs space-y-1">
                <p className="font-semibold text-slate-700 dark:text-slate-200">Filter scope</p>
                <p className="text-muted-foreground">
                  Role: <span className="font-mono font-bold text-slate-800 dark:text-slate-100">{roleFilter === 'all' ? 'All segments' : roleFilter.replace('_', ' ')}</span>
                </p>
              </div>

              {/* Channels */}
              <div className="space-y-2">
                <p className="text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase tracking-wider">Channels</p>
                <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox checked={bulkChannels.email} onCheckedChange={(v) => setBulkChannels((p) => ({ ...p, email: !!v }))} data-testid="bulk-push-channel-email" />
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">Email</span>
                </label>
                <label className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900/40 cursor-pointer">
                  <Checkbox checked={bulkChannels.whatsapp} onCheckedChange={(v) => setBulkChannels((p) => ({ ...p, whatsapp: !!v }))} data-testid="bulk-push-channel-whatsapp" />
                  <MessageCircle className="h-4 w-4 text-emerald-600" />
                  <span className="text-sm">WhatsApp</span>
                </label>
              </div>

              {/* Preview pill */}
              {bulkPreview && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-sm" data-testid="bulk-push-preview">
                  <p className="font-semibold text-amber-900 dark:text-amber-200">
                    {bulkPreview.matched_count === 0
                      ? 'No matching stuck users found for this filter.'
                      : `${bulkPreview.capped_count} user${bulkPreview.capped_count === 1 ? '' : 's'} will be notified.`}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1" onClick={() => setBulkOpen(false)} disabled={bulkLoading}>Cancel</Button>
                <Button
                  variant="outline"
                  className="flex-1"
                  disabled={bulkLoading}
                  data-testid="bulk-push-preview-btn"
                  onClick={async () => {
                    setBulkLoading(true);
                    try {
                      const selected = Object.keys(bulkChannels).filter((k) => bulkChannels[k]);
                      const res = await api.post('/super-admin/adoption/outreach-bulk', {
                        channels: selected.length ? selected : ['email'],
                        role: roleFilter === 'all' ? null : roleFilter,
                        dry_run: true,
                      });
                      setBulkPreview({ matched_count: res.data.matched_count, capped_count: res.data.capped_count });
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Preview failed');
                    } finally {
                      setBulkLoading(false);
                    }
                  }}
                >
                  {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : 'Preview Count'}
                </Button>
                <Button
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  disabled={bulkLoading || (!bulkChannels.email && !bulkChannels.whatsapp) || (bulkPreview && bulkPreview.matched_count === 0)}
                  data-testid="bulk-push-send"
                  onClick={async () => {
                    const selected = Object.keys(bulkChannels).filter((k) => bulkChannels[k]);
                    if (selected.length === 0) { toast.error('Select at least one channel'); return; }
                    setBulkLoading(true);
                    try {
                      const res = await api.post('/super-admin/adoption/outreach-bulk', {
                        channels: selected,
                        role: roleFilter === 'all' ? null : roleFilter,
                        dry_run: false,
                      });
                      setBulkResult(res.data);
                      // Refresh stuck-user list and metrics in the background
                      loadStuck(roleFilter);
                      loadMetrics();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Bulk dispatch failed');
                    } finally {
                      setBulkLoading(false);
                    }
                  }}
                >
                  {bulkLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                  Push Setup to All
                </Button>
              </div>
            </div>
          ) : (
            /* Result panel — per-user audit */
            <div className="space-y-3" data-testid="bulk-push-result">
              <div className="bg-slate-50 dark:bg-slate-900/40 border border-slate-200 dark:border-slate-700 rounded-2xl p-3 flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-100">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span>Relay Dispatch Complete</span>
                </div>
                <div className="flex items-center gap-2 font-mono font-bold text-[11px]" data-testid="bulk-push-result-counter">
                  <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900 px-2 py-0.5 rounded-md">
                    Success: {bulkResult.dispatched_count}
                  </span>
                  {(bulkResult.capped_count - bulkResult.dispatched_count) > 0 && (
                    <span className="bg-rose-50 dark:bg-rose-950/40 text-rose-700 dark:text-rose-300 border border-rose-100 dark:border-rose-900 px-2 py-0.5 rounded-md">
                      Bypassed: {bulkResult.capped_count - bulkResult.dispatched_count}
                    </span>
                  )}
                </div>
              </div>

              <div className="border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/40">
                <div className="bg-slate-50/80 dark:bg-slate-900/60 px-3 py-1.5 border-b border-slate-100 dark:border-slate-800 text-[9px] uppercase font-black tracking-wider text-slate-500 dark:text-slate-400">
                  Detailed Outreach Audit Log
                </div>
                <div className="divide-y divide-slate-100 dark:divide-slate-800 max-h-56 overflow-y-auto text-xs">
                  {(!bulkResult.results || bulkResult.results.length === 0) ? (
                    <div className="p-4 text-center text-slate-400 text-[11px]">No users matched.</div>
                  ) : (
                    bulkResult.results.map((row) => {
                      const dispatched = (row.channels_succeeded || []).length > 0;
                      const errKeys = Object.keys(row.errors || {});
                      const friendlyReason = (() => {
                        if (errKeys.length === 0) return '';
                        for (const k of errKeys) {
                          const v = row.errors[k];
                          if (v === 'no_email_on_file') return 'No email on file';
                          if (v === 'no_phone_on_file') return 'No phone on file';
                        }
                        return `Failed (${String(row.errors[errKeys[0]]).slice(0, 32)})`;
                      })();
                      return (
                        <div key={row.user_id} className="p-2.5 flex items-center justify-between gap-3 hover:bg-slate-50/40 dark:hover:bg-slate-800/40">
                          <div className="space-y-0.5 truncate max-w-[60%]">
                            <div className="font-bold text-slate-800 dark:text-slate-100 truncate">{row.name}</div>
                            <div className="text-[10px] text-slate-400 dark:text-slate-500 font-mono tracking-tight truncate">
                              {row.email || '(no email)'} · {row.role}
                            </div>
                          </div>
                          {dispatched ? (
                            <span className="bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-100 dark:border-emerald-900 text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1">
                              ● Sent ({(row.channels_succeeded || []).map(c => c === 'email' ? 'E' : 'W').join('+')})
                            </span>
                          ) : (
                            <span className="bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-100 dark:border-amber-900 text-[10px] font-bold px-2 py-0.5 rounded-md inline-flex items-center gap-1" title={friendlyReason}>
                              <AlertTriangle className="w-3 h-3" /> {friendlyReason || 'Skipped'}
                            </span>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <Button className="w-full" onClick={() => { setBulkOpen(false); setBulkPreview(null); setBulkResult(null); }} data-testid="bulk-push-close">Close Panel</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
